import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { message, Card, ConfigProvider, theme } from 'antd';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';

import { MODULE_IDS } from '../../utils/constants';
import { formatQty } from '../../utils/formatters';

import { getAllRawMaterials, createRawMaterial, updateRawMaterial, deleteRawMaterial } from '../../api/rawMaterials.api';
import { getUnits } from '../../api/units.api'; 

import StockBadge from '../../components/common/StockBadge';

/* ─────────────────────────────────────────────
   Light Theme Override Styles (Scoped to this page)
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-raw-material {
      /* Overriding global dark variables for this specific page */
      --bg: #f9fafb;
      --surface: #ffffff;
      --surface-2: #f3f4f6;
      --border: #e5e7eb;
      --border-hi: #d1d5db;
      --text-1: #111827;
      --text-2: #374151;
      --text-3: #6b7280;
      --accent: #f59e0b;
      --accent-dim: rgba(245, 158, 11, 0.15);
      --green: #10b981;
    }

    @keyframes rpFadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
    @keyframes rpSlideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
    @keyframes rpPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes rpSpin { to { transform:rotate(360deg) } }

    .rp-live-dot { animation: rpPulse 2s ease-in-out infinite; }
    .rp-drawer-enter { animation: rpSlideIn 0.28s cubic-bezier(0.4,0,0.2,1); }

    .rp-row:hover { background: var(--surface-2) !important; }
    .rp-act-edit:hover  { border-color:#3b82f6 !important; color:#3b82f6 !important; background:rgba(59,130,246,0.1) !important; }
    .rp-act-del:hover   { border-color:#ef4444 !important; color:#ef4444 !important; background:rgba(239,68,68,0.1) !important; }
    .rp-btn-add:hover   { background:#e08d00 !important; }
    .rp-btn-primary:hover:not(:disabled) { background:#e08d00 !important; }
    .rp-btn-ghost:hover { border-color:var(--border-hi) !important; color:var(--text-1) !important; background:var(--surface-2) !important; }
    .rp-search-input:focus { border-color: var(--border-hi) !important; }
    .rp-form-input:focus  { border-color: var(--accent) !important; background: rgba(245,158,11,0.04) !important; }
    .rp-pg-btn-active { background: var(--accent-dim) !important; border-color: var(--accent) !important; color: var(--accent) !important; }
    .rp-pg-btn:hover:not(:disabled):not(.rp-pg-btn-active) { border-color: var(--border-hi) !important; color: var(--text-1) !important; }
    .rp-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .rp-spinner { display:inline-block; width:12px; height:12px; border:2px solid rgba(0,0,0,0.25); border-top-color:var(--text-1); border-radius:50%; animation:rpSpin 0.6s linear infinite; }
    
    /* Native Select arrow styling */
    select.rp-form-input { appearance: none; background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2214%22%20height%3D%2214%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; }

    /* Card Styles Tuning */
    .light-raw-material .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }
    .light-raw-material .ant-card-head {
      border-bottom-color: var(--border);
      padding: 16px 24px;
      min-height: auto;
    }
    .light-raw-material .ant-card-body {
      padding: 24px;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   Inline styles
───────────────────────────────────────────── */
const S = {
  page: { fontFamily: "'Barlow', sans-serif", padding: '4px 0', animation: 'rpFadeUp 0.3s ease' },

  pageTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
  titleDot: { width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', flexShrink: 0 },
  pageSubtitle: { fontSize: 12, color: 'var(--text-3)', marginTop: 4, letterSpacing: '0.3px', fontWeight: 'normal' },
  
  metaChip: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.5px' },
  liveDot: { width: 5, height: 5, background: 'var(--green)', borderRadius: '50%' },

  btnAdd: { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px', transition: 'background var(--transition)' },

  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, maxWidth: 360 },
  searchIcon: { position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' },
  searchInput: { width: '100%', background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '8px 34px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none' },
  searchClear: { position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 },
  
  toggleWrap: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginLeft: 'auto' },
  toggleLabel: { fontSize: 12, fontWeight: 500, color: 'var(--text-2)' },
  toggleTrack: (checked) => ({ width: 32, height: 18, background: checked ? 'var(--accent)' : 'var(--surface-2)', border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-hi)'}`, borderRadius: 12, position: 'relative', transition: 'all 0.2s' }),
  toggleThumb: (checked) => ({ width: 12, height: 12, background: checked ? '#0a0c0f' : 'var(--text-3)', borderRadius: '50%', position: 'absolute', top: 2, left: checked ? 16 : 2, transition: 'all 0.2s' }),

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflowX: 'auto' },
  
  
 tableInner: { minWidth: 1050 },
 tableHead: { display: 'grid', gridTemplateColumns: '40px minmax(140px, 1fr) 100px 90px 90px 110px 140px 110px 80px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' },
  tableRow: { display: 'grid', gridTemplateColumns: '40px minmax(140px, 1fr) 100px 90px 90px 110px 140px 110px 80px', borderBottom: '1px solid var(--border)', transition: 'background var(--transition)', cursor: 'default' },
  th: { padding: '10px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' },
  tableBody: { maxHeight: 500, overflowY: 'auto' },


  
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', overflow: 'visible' },

  actBtn: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', transition: 'all var(--transition)', flexShrink: 0 },
  empty: { padding: '60px 24px', textAlign: 'center' },
  emptyText: { fontSize: 14, color: 'var(--text-3)', marginTop: 8 },

  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)' },
  pgInfo: { fontSize: 12, color: 'var(--text-3)' },
  pgBtns: { display: 'flex', gap: 4 },
  pgBtn: { padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 },
  drawer: { position: 'fixed', right: 0, top: 0, bottom: 0, width: 420, background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 401, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 15px rgba(0,0,0,0.05)' },
  drawerHeader: { padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0, background: 'var(--surface-2)' },
  drawerTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-1)', margin: 0 },
  drawerSubtitle: { fontSize: 11, color: 'var(--text-3)', marginTop: 3 },
  drawerClose: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0 },
  drawerBody: { flex: 1, overflowY: 'auto', padding: 24 },
  drawerFooter: { padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexShrink: 0, background: 'var(--surface-2)' },

  formItem: { marginBottom: 20 },
  formLabelRow: { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 },
  formLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-3)' },
  formRequired: { color: 'var(--accent)', fontSize: 13, lineHeight: 1 },
  charCount: { marginLeft: 'auto', fontSize: 10, color: 'var(--text-3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  formInput: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '9px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none', transition: 'border-color var(--transition)' },
  formInputError: { borderColor: '#ef4444' },
  formHelp: { fontSize: 11, color: 'var(--text-3)', marginTop: 5, letterSpacing: '0.3px', lineHeight: 1.4 },
  formError: { fontSize: 11, color: '#ef4444', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 },

  btnPrimary: { flex: 1, padding: '9px 16px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'background var(--transition)' },
  btnGhost: { padding: '9px 16px', background: 'none', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer', transition: 'all var(--transition)' },

  delPop: { position: 'absolute', right: 36, top: -20, background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: 12, minWidth: 210, zIndex: 50, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  delPopTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 },
  delPopText: { fontSize: 11, color: 'var(--text-3)', marginBottom: 10, lineHeight: 1.5 },
  delPopBtns: { display: 'flex', gap: 6 },
  btnCancelSm: { flex: 1, padding: '5px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnDelSm: { flex: 1, padding: '5px 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontFamily: "'Barlow', sans-serif" },
};

/* ─────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────── */
const Icon = {
  Plus: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Search: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  X: ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Edit: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>,
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  Warning: () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  Box: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
const DeletePopover = ({ itemName, onConfirm, onCancel }) => (
  <div style={S.delPop} onClick={e => e.stopPropagation()}>
    <div style={S.delPopTitle}>Delete Material?</div>
    <div style={S.delPopText}>"{itemName}"<br />This action cannot be undone.</div>
    <div style={S.delPopBtns}>
      <button style={S.btnCancelSm} onClick={onCancel}>Cancel</button>
      <button style={S.btnDelSm} onClick={onConfirm}>Delete</button>
    </div>
  </div>
);

const RawMaterialRow = ({ raw, index, canEdit, canDelete, onEdit, onDelete, isDeleting }) => {
  const [showDelPop, setShowDelPop] = useState(false);
  const delRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (delRef.current && !delRef.current.contains(e.target)) setShowDelPop(false); };
    if (showDelPop) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDelPop]);

  return (
    <div className="rp-row" style={S.tableRow}>
      <div style={{ ...S.td, color: 'var(--text-3)', fontSize: 12 }}>{index}</div>
      <div style={{ ...S.td, color: 'var(--text-1)', fontWeight: 500 }}>{raw.rawName}</div>
      <div style={{ ...S.td, fontSize: 12 }}>{raw.unitDisplayName || '—'}</div>
      <div style={S.td}>{formatQty(raw.openingQty)}</div>
      <div style={S.td}>{formatQty(raw.purchaseQty)}</div>
      <div style={S.td}>{formatQty(raw.consumptionQty)}</div>
      <div style={{ ...S.td, gap: 8 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{formatQty(raw.balanceQty)}</span>
        <StockBadge qty={raw.balanceQty} threshold={10} />
      </div>
      <div style={{ ...S.td, fontSize: 12, color: 'var(--text-3)' }}>{raw.hsnCode || '—'}</div>
      
      <div style={{ ...S.td, gap: 6, position: 'relative' }}>
        {canEdit && (
          <button className="rp-act-edit" style={S.actBtn} onClick={() => onEdit(raw)} title="Edit (Enter)">
            <Icon.Edit />
          </button>
        )}
        {canDelete && (
          <div ref={delRef} style={{ position: 'relative' }}>
            <button className="rp-act-del" style={{ ...S.actBtn, ...(isDeleting ? { borderColor: '#ef4444', color: '#ef4444' } : {}) }} onClick={() => setShowDelPop(v => !v)} title="Delete">
              {isDeleting ? <span className="rp-spinner" style={{ borderColor: 'rgba(239,68,68,0.3)', borderTopColor: '#ef4444' }} /> : <Icon.Trash />}
            </button>
            {showDelPop && (
              <DeletePopover itemName={raw.rawName} onConfirm={() => { onDelete(raw.rawID); setShowDelPop(false); }} onCancel={() => setShowDelPop(false)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RawMaterialDrawer = ({ open, onClose, onSave, editing, loading, unitsData, isFetchingUnits }) => {
  const drawerRef = useRef(null); // 🔴 NAYA: Drawer ko target karne ke liye ref

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: { rawName: '', unitID: '', openingQty: 0, hsnCode: '' },
  });

  const nameVal = watch('rawName') || '';

  useEffect(() => {
    if (open) {
      reset(editing
        ? { rawName: editing.rawName, unitID: editing.unitID || '', openingQty: editing.openingQty || 0, hsnCode: editing.hsnCode || '' }
        : { rawName: '', unitID: '', openingQty: 0, hsnCode: '' }
      );

      // 🔴 NAYA: Drawer khulte hi pehle input (Material Name) par cursor lane ke liye
      setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input[name="rawName"]');
        if (firstInput) firstInput.focus();
      }, 150);
    }
  }, [open, editing, reset]);

  // 🔴 NAYA: Keyboard Navigation Logic (Enter-to-Next-Field)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (!drawerRef.current || !drawerRef.current.contains(e.target)) return;

      // Drawer ke andar ke inputs, SELECT dropdowns, aur primary Save button dhoondo
      const focusable = drawerRef.current.querySelectorAll(
        'input:not([disabled]):not([readonly]), ' +
        'select:not([disabled]), ' + // <-- Select tag ko add kiya hai taaki Unit par bhi focus ruke
        'button.rp-btn-primary:not([disabled])'
      );
      
      const elementsArr = Array.from(focusable);
      const activeIndex = elementsArr.indexOf(document.activeElement);

      if (activeIndex === -1) return;

      // Agar 'Save Material' button par Enter dabaya hai, toh form submit hone do
      if (document.activeElement.classList.contains('rp-btn-primary')) {
        return; 
      }

      // Default submit roko aur aage wale field par jump karo
      e.preventDefault(); 
      const nextIndex = activeIndex + 1;
      if (nextIndex < elementsArr.length) {
        const nextEl = elementsArr[nextIndex];
        nextEl.focus();
        // Agar next element text input hai toh select kar lo, select tag par ye zarurat nahi
        if (nextEl.tagName === 'INPUT') {
          nextEl.select(); 
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div style={S.overlay} onClick={onClose} />
      {/* 🔴 NAYA: Yahan ref={drawerRef} add kiya hai */}
      <div style={S.drawer} className="rp-drawer-enter" ref={drawerRef}>
        <div style={S.drawerHeader}>
          <div>
            <div style={S.drawerTitle}>{editing ? 'Edit Raw Material' : 'Add Raw Material'}</div>
            <div style={S.drawerSubtitle}>{editing ? 'Modify the inventory item below' : 'Fill in the details below'}</div>
          </div>
          <button style={S.drawerClose} className="rp-act-del" onClick={onClose}><Icon.X /></button>
        </div>

        <div style={S.drawerBody}>
          <div style={S.formItem}>
            <div style={S.formLabelRow}>
              <span style={S.formLabel}>Material Name</span>
              <span style={S.formRequired}>*</span>
              <span style={S.charCount}>{nameVal.length} / 100</span>
            </div>
            <Controller
              name="rawName" control={control}
              rules={{ required: 'Name is required', maxLength: { value: 100, message: 'Max 100 characters' } }}
              render={({ field }) => (
                <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.rawName ? S.formInputError : {}) }} placeholder="e.g. Empty Plastic Bottle 1L" maxLength={100} autoComplete="off" />
              )}
            />
            {errors.rawName && <div style={S.formError}><Icon.Warning />{errors.rawName.message}</div>}
          </div>

          <div style={S.formItem}>
            <div style={S.formLabelRow}>
              <span style={S.formLabel}>Unit</span>
              <span style={S.formRequired}>*</span>
            </div>
            <Controller
              name="unitID" control={control}
              rules={{ required: 'Unit is required' }}
              render={({ field }) => (
                <select {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.unitID ? S.formInputError : {}) }}>
                  <option value="" disabled>{isFetchingUnits ? 'Loading units...' : 'Select Unit...'}</option>
                  {unitsData.map(u => (
                    <option key={u.unitID} value={u.unitID}>{u.purchaseUnit} {u.consumptionUnit ? `/ ${u.consumptionUnit}` : ''}</option>
                  ))}
                </select>
              )}
            />
            {errors.unitID && <div style={S.formError}><Icon.Warning />{errors.unitID.message}</div>}
          </div>

          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Opening Quantity</span></div>
            <Controller
              name="openingQty" control={control}
              rules={{ min: { value: 0, message: 'Cannot be negative' } }}
              render={({ field }) => (
                <input {...field} type="number" min="0" step="any" className="rp-form-input" style={{ ...S.formInput, ...(errors.openingQty ? S.formInputError : {}) }} placeholder="0" />
              )}
            />
            {errors.openingQty ? <div style={S.formError}><Icon.Warning />{errors.openingQty.message}</div> : <div style={S.formHelp}>* Set once. Stock updates automatically from purchases.</div>}
          </div>

          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>HSN Code</span></div>
            <Controller
              name="hsnCode" control={control}
              rules={{ maxLength: { value: 20, message: 'Max 20 characters' } }}
              render={({ field }) => (
                <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.hsnCode ? S.formInputError : {}) }} placeholder="e.g. 39233090" maxLength={20} autoComplete="off" />
              )}
            />
            {errors.hsnCode && <div style={S.formError}><Icon.Warning />{errors.hsnCode.message}</div>}
          </div>
        </div>

        <div style={S.drawerFooter}>
          <button style={S.btnGhost} className="rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} className="rp-btn-primary" onClick={handleSubmit(onSave)} disabled={loading}>
            {loading ? <><span className="rp-spinner" />Saving...</> : <><Icon.Check />Save Material</>}
          </button>
        </div>
      </div>
    </>
  );
};
/* ─────────────────────────────────────────────
   Main Page Component
───────────────────────────────────────────── */
const RawMaterialPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRaw, setEditingRaw] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 8 });
  const searchRef = useRef(null);

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_MATERIAL);

  /* Queries */
  const { data, isLoading } = useQuery({
    queryKey: ['rawMaterials', plantId, searchText, tableParams.current, tableParams.pageSize, lowStockOnly],
    queryFn: () => getAllRawMaterials(plantId, {
      search: searchText, page: tableParams.current, pageSize: tableParams.pageSize, lowStock: lowStockOnly ? true : undefined
    }),
    enabled: !!plantId,
  });

  const rawMaterials = data?.items || [];
  const totalCount = data?.total || 0;

  const { data: unitsData = [], isLoading: isFetchingUnits } = useQuery({
    queryKey: ['units', plantId], queryFn: () => getUnits(plantId), enabled: !!plantId,
  });

  /* Mutations */
  const createMutation = useMutation({
    mutationFn: (d) => createRawMaterial(plantId, d),
    onSuccess: () => { message.success('Raw Material created successfully!'); queryClient.invalidateQueries({ queryKey: ['rawMaterials'] }); closeDrawer(); },
    onError: (err) => message.error(err.message?.includes('exists') ? 'Material already exists.' : err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => updateRawMaterial(plantId, editingRaw.rawID, d),
    onSuccess: () => { message.success('Raw Material updated successfully!'); queryClient.invalidateQueries({ queryKey: ['rawMaterials'] }); closeDrawer(); },
    onError: (err) => message.error(err.message?.includes('exists') ? 'Material already exists.' : err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (rawId) => deleteRawMaterial(plantId, rawId),
    onSuccess: () => { message.success('Raw Material deleted successfully!'); queryClient.invalidateQueries({ queryKey: ['rawMaterials'] }); },
    onError: (err) => message.error(err.message || 'Cannot delete material. It is in use.'),
  });

  /* Handlers */
  const openDrawer = (record = null) => { setEditingRaw(record); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setEditingRaw(null); };
  
  const onSave = (formData) => {
    const payload = { ...formData, openingQty: Number(formData.openingQty) || 0 };
    if (editingRaw) updateMutation.mutate(payload); else createMutation.mutate(payload);
  };

  const handleSearch = (val) => { setSearchText(val); setTableParams(prev => ({ ...prev, current: 1 })); };


  const totalPages = Math.max(1, Math.ceil(totalCount / tableParams.pageSize));
  const getPageNums = () => {
    const p = tableParams.current, t = totalPages;
    if (t <= 5) return Array.from({ length: t }, (_, i) => i + 1);
    if (p <= 3) return [1, 2, 3, 4, 5];
    if (p >= t - 2) return [t - 4, t - 3, t - 2, t - 1, t];
    return [p - 2, p - 1, p, p + 1, p + 2];
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-raw-material" style={S.page}>

        <Card
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={S.pageTitle}>
                <span style={S.titleDot} />
                Raw Material Master
              </div>
              <div style={S.pageSubtitle}>Manage inventory items, opening stock, and HSN codes</div>
            </div>
          }
          extra={
            canAdd && (
              <button className="rp-btn-add" style={S.btnAdd} onClick={() => openDrawer(null)}>
                <Icon.Plus /> Add Material
              </button>
            )
          }
          styles={{ header: { padding: '20px 24px' } }}
        >
          
          {/* Toolbar */}
          <div style={S.toolbar}>
            <div style={S.searchWrap}>
              <span style={S.searchIcon}><Icon.Search /></span>
              <input ref={searchRef} className="rp-search-input" style={S.searchInput} placeholder="Search raw material... (Ctrl+F)" value={searchText} onChange={e => handleSearch(e.target.value)} />
              {searchText && <button style={S.searchClear} onClick={() => handleSearch('')}><Icon.X size={11} /></button>}
            </div>
            
            <div style={S.metaChip}>
              <span className="rp-live-dot" style={S.liveDot} />
              {totalCount} item{totalCount !== 1 ? 's' : ''}
            </div>
            
            {/* Custom Toggle Switch for Low Stock */}
            <label style={S.toggleWrap}>
              <input type="checkbox" checked={lowStockOnly} onChange={(e) => { setLowStockOnly(e.target.checked); setTableParams(prev => ({ ...prev, current: 1 })); }} style={{ display: 'none' }} />
              <span style={S.toggleLabel}>Low Stock Only</span>
              <div style={S.toggleTrack(lowStockOnly)}><div style={S.toggleThumb(lowStockOnly)} /></div>
            </label>
          </div>

          {/* Table */}
          <div style={S.tableWrap}>
            <div style={S.tableInner}>
              <div style={S.tableHead}>
                {['#', 'Raw Name', 'Unit', 'Opening', 'Purchase', 'Consumed', 'Balance', 'HSN Code', 'Actions'].map(h => (
                  <div key={h} style={S.th}>{h}</div>
                ))}
              </div>
              
              <div style={S.tableBody}>
                {isLoading ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <span className="rp-spinner" />
                  </div>
                ) : rawMaterials.length === 0 ? (
                  <div style={S.empty}>
                    <div style={{ color: 'var(--text-3)', marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Icon.Box /></div>
                    <div style={S.emptyText}>{searchText || lowStockOnly ? 'No materials found matching criteria.' : 'No materials yet. Click "Add Material" to get started.'}</div>
                  </div>
                ) : (
                  rawMaterials.map((p, i) => (
                    <RawMaterialRow
                      key={p.rawID} raw={p} index={(tableParams.current - 1) * tableParams.pageSize + i + 1}
                      canEdit={canEdit} canDelete={canDelete} onEdit={openDrawer}
                      onDelete={(rawID) => deleteMutation.mutate(rawID)}
                      isDeleting={deleteMutation.isPending && deleteMutation.variables === p.rawID}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalCount > tableParams.pageSize && (
              <div style={S.pagination}>
                <div style={S.pgInfo}>Showing {(tableParams.current - 1) * tableParams.pageSize + 1}–{Math.min(tableParams.current * tableParams.pageSize, totalCount)} of {totalCount}</div>
                <div style={S.pgBtns}>
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === 1} onClick={() => setTableParams(p => ({ ...p, current: p.current - 1 }))}>‹ Prev</button>
                  {getPageNums().map(n => (
                    <button key={n} className={`rp-pg-btn${n === tableParams.current ? ' rp-pg-btn-active' : ''}`} style={S.pgBtn} onClick={() => setTableParams(p => ({ ...p, current: n }))}>{n}</button>
                  ))}
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === totalPages} onClick={() => setTableParams(p => ({ ...p, current: p.current + 1 }))}>Next ›</button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Drawer */}
        <RawMaterialDrawer
          open={drawerOpen} onClose={closeDrawer} onSave={onSave} editing={editingRaw} loading={isMutating}
          unitsData={unitsData} isFetchingUnits={isFetchingUnits}
        />
      </div>
    </ConfigProvider>
  );
};

export default RawMaterialPage;